import React from 'react';
import { motion } from 'framer-motion';

export default function Hero() {
  return (
    <section className="relative min-h-[90vh] lg:min-h-screen flex items-center pt-20 bg-[var(--off-white)] overflow-hidden">
      {/* Decorative Textures & Background Gradients */}
      <div className="absolute top-0 right-0 w-full h-full pointer-events-none opacity-40">
        <div className="absolute top-[-10%] right-[-5%] w-[600px] h-[600px] bg-[var(--kraft)] rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[400px] h-[400px] bg-[var(--mint)] rounded-full blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 w-full grid lg:grid-cols-2 gap-12 lg:gap-24 items-center relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }} 
          className="max-w-2xl text-center lg:text-left order-2 lg:order-1"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1 mb-8 rounded-full bg-[var(--navy)] text-[var(--mint)] text-[10px] font-bold tracking-[0.2em] uppercase">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--mint)] animate-pulse" /> 
            Circular Home Care Experience
          </div>
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold text-[var(--navy)] leading-[0.95] mb-8 tracking-tighter italic">
            Refill <br className="hidden lg:block"/> Not <span className="text-[var(--mint-dark)]">Replace.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed max-w-lg mx-auto lg:mx-0">
            Ditch the plastic jugs for premium amber glass vessels and plant-based refills. Better for your home, lighter on the planet.
          </p>
          <div className="flex flex-col sm:row justify-center lg:justify-start gap-4">
            <a href="#shop" className="px-10 py-5 bg-[var(--navy)] text-[var(--mint)] font-bold hover:bg-[var(--navy-light)] transition-all duration-300 rounded-sm shadow-xl hover:shadow-2xl hover:-translate-y-1 text-center">
              Shop Starter Kits
            </a>
            <a href="#how-it-works" className="px-10 py-5 bg-white text-[var(--navy)] font-bold border border-gray-200 hover:border-[var(--navy)] transition-all duration-300 rounded-sm text-center">
              Explore The Loop
            </a>
          </div>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.98 }} 
          animate={{ opacity: 1, scale: 1 }} 
          transition={{ duration: 1.2, delay: 0.2 }} 
          className="relative h-[500px] md:h-[650px] lg:h-[750px] w-full order-1 lg:order-2 flex items-center justify-center"
        >
          {/* Main Hero Image: Amber Bottles in Tiled Utility Space */}
          <div className="relative w-full h-full group">
            <div className="absolute inset-0 border-2 border-[var(--kraft)] translate-x-4 translate-y-4 rounded-2xl -z-10 hidden md:block" />
            <picture>
              <source 
                srcSet="https://images.unsplash.com/photo-1621459544210-9832149d8576?auto=format&fit=crop&q=85&w=800 800w, https://images.unsplash.com/photo-1621459544210-9832149d8576?auto=format&fit=crop&q=85&w=1600 1600w" 
                sizes="(max-width: 1024px) 100vw, 50vw" 
                type="image/webp" 
              />
              <img 
                src="https://images.unsplash.com/photo-1621459544210-9832149d8576?auto=format&fit=crop&q=80&w=1600" 
                alt="Premium amber glass refill vessels on a clean tiled utility surface" 
                className="w-full h-full object-cover rounded-2xl shadow-2xl"
                width="800"
                height="1000"
                fetchpriority="high"
              />
            </picture>

            {/* Supporting Product Crop: Natural Brush and Soap Detail */}
            <motion.div 
              initial={{ x: 30, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="absolute -bottom-6 -left-6 md:-bottom-10 md:-left-10 w-32 h-32 md:w-48 md:h-48 bg-white p-2 rounded-xl shadow-2xl hidden sm:block z-20"
            >
              <img 
                src="https://images.unsplash.com/photo-1605265002939-556157077aee?auto=format&fit=crop&q=80&w=400" 
                alt="Detail of natural wood cleaning brush and solid soap" 
                className="w-full h-full object-cover rounded-lg"
                width="200"
                height="200"
                loading="eager"
              />
              <div className="absolute inset-0 rounded-lg ring-1 ring-inset ring-black/5" />
            </motion.div>

            {/* Floating Label for Supporting Crop */}
            <div className="absolute -bottom-12 -left-4 md:-bottom-16 md:-left-8 hidden sm:block">
              <span className="text-[9px] font-bold uppercase tracking-[0.3em] text-[var(--navy)] opacity-40">System Components</span>
            </div>
          </div>

          {/* Decorative circular floaty elements */}
          <motion.div 
            animate={{ y: [0, -15, 0] }} 
            transition={{ repeat: Infinity, duration: 6, ease: "easeInOut" }} 
            className="absolute -top-10 -right-10 w-40 h-40 bg-[var(--mint)] rounded-full mix-blend-multiply opacity-60 blur-2xl hidden lg:block" 
          />
        </motion.div>
      </div>
    </section>
  );
}